/**
 * ISO 3166-1 alpha-3 → alpha-2 country-code normalization.
 *
 * Azure Document Intelligence's prebuilt-idDocument model returns the
 * `CountryRegion` field as ISO 3166-1 alpha-3 ("MYS" / "USA" / "IND"),
 * while the v3 frontend's CountrySelect uses alpha-2 ("MY" / "US" / "IN").
 * The mismatch silently dropped the auto-fill on the Country of Citizenship
 * dropdown — the pill row showed the raw alpha-3 but the dropdown couldn't
 * find a matching <option> so the field rendered empty.
 *
 * This helper bridges the gap. Pass any value through `normalizeIso2`:
 *   - already 2 chars → upper-case + return
 *   - 3 chars and present in the map → return alpha-2
 *   - anything else → upper-case the input and return as-is (no value loss)
 *
 * The map is the canonical ISO 3166-1 alpha-3 → alpha-2 mapping. Static
 * data, doesn't change. Source: ISO 3166-1 official lists.
 */

const ISO3_TO_ISO2: Readonly<Record<string, string>> = {
  AFG: 'AF', ALB: 'AL', DZA: 'DZ', ASM: 'AS', AND: 'AD', AGO: 'AO',
  AIA: 'AI', ATA: 'AQ', ATG: 'AG', ARG: 'AR', ARM: 'AM', ABW: 'AW',
  AUS: 'AU', AUT: 'AT', AZE: 'AZ', BHS: 'BS', BHR: 'BH', BGD: 'BD',
  BRB: 'BB', BLR: 'BY', BEL: 'BE', BLZ: 'BZ', BEN: 'BJ', BMU: 'BM',
  BTN: 'BT', BOL: 'BO', BIH: 'BA', BWA: 'BW', BRA: 'BR', BRN: 'BN',
  BGR: 'BG', BFA: 'BF', BDI: 'BI', CPV: 'CV', KHM: 'KH', CMR: 'CM',
  CAN: 'CA', CYM: 'KY', CAF: 'CF', TCD: 'TD', CHL: 'CL', CHN: 'CN',
  COL: 'CO', COM: 'KM', COG: 'CG', COD: 'CD', COK: 'CK', CRI: 'CR',
  CIV: 'CI', HRV: 'HR', CUB: 'CU', CYP: 'CY', CZE: 'CZ', DNK: 'DK',
  DJI: 'DJ', DMA: 'DM', DOM: 'DO', ECU: 'EC', EGY: 'EG', SLV: 'SV',
  GNQ: 'GQ', ERI: 'ER', EST: 'EE', SWZ: 'SZ', ETH: 'ET', FJI: 'FJ',
  FIN: 'FI', FRA: 'FR', GUF: 'GF', PYF: 'PF', GAB: 'GA', GMB: 'GM',
  GEO: 'GE', DEU: 'DE', GHA: 'GH', GIB: 'GI', GRC: 'GR', GRL: 'GL',
  GRD: 'GD', GLP: 'GP', GUM: 'GU', GTM: 'GT', GIN: 'GN', GNB: 'GW',
  GUY: 'GY', HTI: 'HT', HND: 'HN', HKG: 'HK', HUN: 'HU', ISL: 'IS',
  IND: 'IN', IDN: 'ID', IRN: 'IR', IRQ: 'IQ', IRL: 'IE', ISR: 'IL',
  ITA: 'IT', JAM: 'JM', JPN: 'JP', JOR: 'JO', KAZ: 'KZ', KEN: 'KE',
  KIR: 'KI', PRK: 'KP', KOR: 'KR', KWT: 'KW', KGZ: 'KG', LAO: 'LA',
  LVA: 'LV', LBN: 'LB', LSO: 'LS', LBR: 'LR', LBY: 'LY', LIE: 'LI',
  LTU: 'LT', LUX: 'LU', MAC: 'MO', MKD: 'MK', MDG: 'MG', MWI: 'MW',
  MYS: 'MY', MDV: 'MV', MLI: 'ML', MLT: 'MT', MHL: 'MH', MTQ: 'MQ',
  MRT: 'MR', MUS: 'MU', MYT: 'YT', MEX: 'MX', FSM: 'FM', MDA: 'MD',
  MCO: 'MC', MNG: 'MN', MNE: 'ME', MSR: 'MS', MAR: 'MA', MOZ: 'MZ',
  MMR: 'MM', NAM: 'NA', NRU: 'NR', NPL: 'NP', NLD: 'NL', NCL: 'NC',
  NZL: 'NZ', NIC: 'NI', NER: 'NE', NGA: 'NG', NIU: 'NU', NFK: 'NF',
  MNP: 'MP', NOR: 'NO', OMN: 'OM', PAK: 'PK', PLW: 'PW', PSE: 'PS',
  PAN: 'PA', PNG: 'PG', PRY: 'PY', PER: 'PE', PHL: 'PH', POL: 'PL',
  PRT: 'PT', PRI: 'PR', QAT: 'QA', REU: 'RE', ROU: 'RO', RUS: 'RU',
  RWA: 'RW', BLM: 'BL', SHN: 'SH', KNA: 'KN', LCA: 'LC', MAF: 'MF',
  SPM: 'PM', VCT: 'VC', WSM: 'WS', SMR: 'SM', STP: 'ST', SAU: 'SA',
  SEN: 'SN', SRB: 'RS', SYC: 'SC', SLE: 'SL', SGP: 'SG', SXM: 'SX',
  SVK: 'SK', SVN: 'SI', SLB: 'SB', SOM: 'SO', ZAF: 'ZA', SSD: 'SS',
  ESP: 'ES', LKA: 'LK', SDN: 'SD', SUR: 'SR', SWE: 'SE', CHE: 'CH',
  SYR: 'SY', TWN: 'TW', TJK: 'TJ', TZA: 'TZ', THA: 'TH', TLS: 'TL',
  TGO: 'TG', TKL: 'TK', TON: 'TO', TTO: 'TT', TUN: 'TN', TUR: 'TR',
  TKM: 'TM', TCA: 'TC', TUV: 'TV', UGA: 'UG', UKR: 'UA', ARE: 'AE',
  GBR: 'GB', USA: 'US', URY: 'UY', UZB: 'UZ', VUT: 'VU', VAT: 'VA',
  VEN: 'VE', VNM: 'VN', VGB: 'VG', VIR: 'VI', WLF: 'WF', ESH: 'EH',
  YEM: 'YE', ZMB: 'ZM', ZWE: 'ZW',
};

/**
 * Normalize a country code to ISO 3166-1 alpha-2.
 *
 * Returns the input upper-cased when the value is already 2 chars or when
 * the alpha-3 isn't in the map — never returns empty for a non-empty input,
 * so we never lose data.
 */
export function normalizeIso2(input: string | null | undefined): string {
  if (!input) return '';
  const trimmed = input.trim().toUpperCase();
  if (trimmed.length === 2) return trimmed;
  if (trimmed.length === 3 && ISO3_TO_ISO2[trimmed]) return ISO3_TO_ISO2[trimmed]!;
  return trimmed;
}
